<template>
  <div
    v-if="data !== null"
    class="global-pagination col-12"
    data-grid
  >
    <button
      v-if="page > 3"
      data-grid="center-center"
      class="first space"
      @click="page = 1"
    >
      1
    </button>

    <span
      v-if="page > 3"
      class="points space"
    >
      ..
    </span>

    <button
      v-if="page > 1"
      data-grid="center-center"
      class="space"
      @click="page--"
    >
      <ChevronLeftIcon />
    </button>

    <button
      v-for="p in pageRange"
      :key="p"
      class="space"
      :class="{ 'active': p === data.current_page }"
      data-grid="center-center"
      @click="page = p"
    >
      {{ p }}
    </button>

    <button
      v-if="page < data.last_page"
      data-grid="center-center"
      class="space"
      @click="page++"
    >
      <ChevronRightIcon />
    </button>

    <span
      v-if="page < data.last_page - 2"
      class="points space"
    >..</span>

    <button
      v-if="page < data.last_page - 2"
      data-grid="center-center"
      class="space"
      @click="page = data.last_page"
    >
      {{ data.last_page }}
    </button>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import ChevronLeftIcon from '../icons/ChevronLeftIcon.vue'
import ChevronRightIcon from '../icons/ChevronRightIcon.vue'
import validatorMixin from '../core/utils/validator'

export default defineComponent({
  name: 'Pagination',

  components: {
    ChevronLeftIcon,
    ChevronRightIcon
  },

  mixins: [validatorMixin],

  props: {
    action: {
      type: String,
      required: true
    },

    getter: {
      type: String,
      required: true
    },

    rules: {
      type: Object,
      required: true
    },

    perPage: {
      type: Number,
      required: false,
      default: () => 6
    },

    filter: {
      type: String,
      required: false,
      default: () => ''
    }
  },

  emits: ['loading'],

  data: () => ({
    page: 1,
    loading: false,
    changing: false
  }),

  computed: {
    data () {
      return this.$store.getters[this.getter]
    },

    list () {
      return this.data.list
    },

    filters () {
      return this.getFilters(this.filter)
    },

    pageRange () {
      let pages = []

      for (let index = 1, len = this.data.last_page; index <= len; index++) {
        pages.push(index)
      }

      const current = this.data.current_page
      const last = this.data.last_page

      if (current > 1 && current < last) {
        pages = pages.slice(current - 2, current + 1)
      } else if (current === last) {
        pages = pages.slice(current - 3, current)
      } else if (current === 1) {
        pages = pages.slice(current - 1, current + 2)
      }

      return pages
    }
  },

  watch: {
    page () {
      this.search()
    },

    data: {
      deep: true,
      handler () {
        this.emitter.emit('update-all')
      }
    },

    loading (status) {
      this.$emit('loading', status)
    }
  },

  mounted () {
    this.emitter.on(`${this.filter}-filter-changed`, this.filterChanged)
    if (this.$route.query.page) {
      this.page = +this.$route.query.page
    }
    this.search(true)
  },

  unmounted () {
    this.emitter.off(`${this.filter}-filter-changed`, this.filterChanged)
  },

  methods: {
    filterChanged () {
      this.changing = true
      this.page = 1
      this.changing = false

      this.$forceUpdate()
      this.search()
    },

    async search (initialSearch = false) {
      if (this.changing) return

      this.loading = true
      const { page, perPage } = this
      const filters = this.getFilters(this.filter)
      const body = { page, per_page: perPage, ...filters }

      if (!this.validate(body)) return

      if (!initialSearch && +this.$route.query.page !== page) {
        const { $route } = this
        const { query } = $route
        this.$router.replace({ ...$route, query: { ...query, page } })
      }

      await this.$store.dispatch(this.action, body)
      this.loading = false
    },

    validate (body) {
      const { $validator, rules } = this
      const errors = $validator(body, rules)

      return Object.keys(errors || {}).length <= 0
    }
  }
})
</script>

<style lang="scss" scoped>
  .global-pagination {
    padding: 20px 0;
    justify-content: center;

    button {
      width: 35px;
      min-width: 35px;
      padding: 0;
      height: 35px;
      border-radius: 4px;
      margin: 0;
      border: 1px solid rgb(var(--gray7));
      background: rgb(var(--white));
      color: rgb(var(--gray8));
      font-size: 1.3rem;

      &.active {
        background: var(--primary);
        border-color: var(--primary);
        color: rgb(var(--white));
      }

      svg {
        width: 15px;
      }
    }

    .space {
      &:not(:last-child) {
        margin-right: 8px
      }
    }
  }
</style>
